import { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/shared/integrations/supabase/client";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { onAuthStateChanged, type User as FirebaseUser } from "@firebase/auth";
import * as Sentry from "@sentry/react";
import { setAnalyticsUser, setAnalyticsUserProperties } from "@/shared/integrations/firebase/analytics";
import { requireUserIdForDatabase } from "@/shared/lib/userIdUtils";
import { stripAppBasePath, withAppBasePath } from "@/shared/lib/appBasePath";
import { ensureFreshToken } from "@/shared/lib/ensureFreshToken";
import { setPlatformAdminClaim } from "@/shared/lib/adminUids";
import { captureQueryError } from "@/shared/integrations/sentry/reportQueryError";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  user_type: "admin" | "property_owner" | "home_association" | "facilities_management" | "service_provider";
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  redirectToUserDashboard: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const firebaseUnsubscribeRef = useRef<(() => void) | null>(null);

  const fetchUserProfile = async (_userId: string) => {
    try {
      // Ensure Firebase user is authenticated
      if (!firebaseAuth.currentUser) {
        return null;
      }

      // Get Firebase token first to ensure it's available for Supabase
      const firebaseToken = await firebaseAuth.currentUser.getIdToken();
      if (!firebaseToken) {
        return null;
      }

      const supabaseUserId = requireUserIdForDatabase();

      // Use maybeSingle() instead of single() to avoid 400 error when RLS blocks (0 rows)
      const { data, error } = await supabase.from("profiles").select("*").eq("user_id", supabaseUserId).maybeSingle();

      if (error) {
        console.error("Error fetching user profile:", error);
        captureQueryError({ table: "profiles", operation: "select", error });
        return null;
      }

      if (!data) {
        return null;
      }

      return data as UserProfile;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  const redirectToUserDashboard = () => {
    try {
      const path = stripAppBasePath(window.location.pathname);
      // If already within the app area, do nothing to prevent bouncing
      if (path.startsWith("/dashboard") || path.startsWith("/properties") || path.startsWith("/property") || path.startsWith("/assets") || path.startsWith("/asset") || path.startsWith("/developer") || path.startsWith("/exports")) return;

      // Decide target based on profile (defaults to /dashboard)
      const target = withAppBasePath("/dashboard");
      if (window.location.pathname !== withAppBasePath("/dashboard")) {
        window.location.href = target;
      }
    } catch (e) {
      console.error("redirectToUserDashboard error:", e);
    }
  };

  useEffect(() => {
    // COMMENTED OUT: Supabase auth state listener - we're only using Firebase auth
    // const {
    //   data: { subscription },
    // } = supabase.auth.onAuthStateChange(async (event, session) => {
    //   setSession(session);
    //   setUser(session?.user ?? null);

    //   if (session?.user) {
    //     // Fetch user profile when user signs in
    //     setTimeout(async () => {
    //       const userProfile = await fetchUserProfile(session.user.id);
    //       setProfile(userProfile);
    //       setLoading(false);

    //       // Log activity and redirect
    //       if (event === "SIGNED_IN") {
    //         supabase.from("user_activity_logs").insert({
    //           user_id: session.user.id,
    //           activity_type: "login",
    //           description: "User signed in",
    //           ip_address: "",
    //           user_agent: navigator.userAgent,
    //         });
    //         redirectToUserDashboard();
    //       }
    //     }, 0);
    //   } else {
    //     setProfile(null);
    //     setLoading(false);
    //   }
    // });

    // Firebase auth state listener
    // When Firebase user signs in, we use the Firebase user directly
    // Supabase will automatically use the Firebase token via the accessToken function for database queries
    const handleFirebaseAuthState = async (firebaseUser: FirebaseUser | null) => {
      // This function is called by onAuthStateChanged
      // onAuthStateChanged fires immediately when set up, so this handles the initial auth state
      // firebaseAuth.currentUser is always available after this finishes loading

      if (firebaseUser) {
        // Firebase user signed in - use Firebase user info directly
        // Pull the latest custom claims (e.g. organization_id) into the token
        // before any Supabase query runs.
        await ensureFreshToken(firebaseUser);
        // Cache the platform-admin claims so the synchronous isAdminUser() can
        // honor them (claim-admins/contractors behave like hardcoded ADMIN_UIDS).
        try {
          const { claims } = await firebaseUser.getIdTokenResult();
          setPlatformAdminClaim(firebaseUser.uid, {
            admin: claims.admin === true,
            contractor: claims.contractor === true,
          });
          const orgId = (claims.organization_id as string | undefined) ?? "none";
          Sentry.setTag("organization_id", orgId);
          // GA4 user property for segmentation by organization.
          setAnalyticsUserProperties({ organization_id: orgId });
        } catch {
          setPlatformAdminClaim(null, null);
        }

        // Attribute Sentry issues and analytics events to this user. Send only
        // the uid — no email/PII in third-party telemetry (no scrubber is set).
        Sentry.setUser({ id: firebaseUser.uid });
        setAnalyticsUser(firebaseUser.uid);
        try {
          // Create a User-like object from Firebase user for compatibility
          const supabaseUser = {
            id: firebaseUser.uid,
            email: firebaseUser.email || undefined,
            user_metadata: { full_name: firebaseUser.displayName || undefined },
          } as unknown as User;

          setUser(supabaseUser);
          setSession({
            user: supabaseUser,
            access_token: "",
            refresh_token: "",
            expires_in: 0,
            expires_at: 0,
            token_type: "bearer",
          } as Session);

          // Fetch user profile asynchronously
          fetchUserProfile(firebaseUser.uid)
            .then((userProfile) => {
              setProfile(userProfile);
            })
            .catch((error) => {
              console.error("Error fetching user profile:", error);
            });

          // Log activity (but don't redirect if we're already on a page)
          try {
            const supabaseUserId = requireUserIdForDatabase();
            await supabase.from("user_activity_logs").insert({
              user_id: supabaseUserId,
              activity_type: "login",
              description: "User signed in via Firebase",
              ip_address: "",
              user_agent: navigator.userAgent,
            });
            // Only redirect if we're on the home page or demo page
            // Exclude /signin so the Login component can mount and force a sign-out
            if (
              stripAppBasePath(window.location.pathname) === "/" ||
              stripAppBasePath(window.location.pathname) === "/demo"
            ) {
              redirectToUserDashboard();
            }
          } catch (e) {
            // ignore log errors
          }
        } catch (error) {
          console.error("Error handling Firebase sign-in:", error);
        } finally {
          // Set loading to false after auth state has been handled
          // firebaseAuth.currentUser is now available after this finishes
          setLoading(false);
        }
      } else {
        // Firebase user signed out
        setPlatformAdminClaim(null, null);
        Sentry.setUser(null);
        setAnalyticsUser(null);
        setUser(null);
        setSession(null);
        setProfile(null);
        setLoading(false);
      }
    };

    try {
      if (firebaseAuth) {
        // Note: firebaseAuth.currentUser may not be available yet - wait for listener to fire
        // onAuthStateChanged fires immediately when set up and will call handleFirebaseAuthState
        // handleFirebaseAuthState will set loading to false when it finishes
        // After that, firebaseAuth.currentUser will always be available

        // Set up Firebase auth state listener
        // onAuthStateChanged fires immediately with the current user when set up
        // It will call handleFirebaseAuthState which sets loading to false when done
        firebaseUnsubscribeRef.current = onAuthStateChanged(firebaseAuth, handleFirebaseAuthState);
      } else {
        setLoading(false);
      }
    } catch (error) {
      // Firebase not available or not configured - this is okay, just skip Firebase integration
      console.warn("Firebase Auth not available, skipping Firebase auth state listener:", error);
      setLoading(false);
    }

    // COMMENTED OUT: Check for existing Supabase session - we're only using Firebase auth
    // supabase.auth.getSession().then(async ({ data: { session } }) => {
    //   setSession(session);
    //   setUser(session?.user ?? null);

    //   if (session?.user) {
    //     const userProfile = await fetchUserProfile(session.user.id);
    //     setProfile(userProfile);
    //   }

    //   setLoading(false);
    // });

    // Initial Firebase auth state check is now handled in the listener setup above

    return () => {
      // COMMENTED OUT: Supabase subscription cleanup
      // subscription.unsubscribe();
      if (firebaseUnsubscribeRef.current) {
        firebaseUnsubscribeRef.current();
        firebaseUnsubscribeRef.current = null;
      }
    };
  }, []);

  const signOut = async () => {
    try {
      // Log sign out activity
      if (user) {
        try {
          const supabaseUserId = requireUserIdForDatabase();
          await supabase.from("user_activity_logs").insert({
            user_id: supabaseUserId,
            activity_type: "logout",
            description: "User signed out",
            ip_address: "",
            user_agent: navigator.userAgent,
          });
        } catch (e) {
          // ignore log errors
        }
      }

      // Thorough cleanup and sign out
      try {
        const { cleanupAuthState } = await import("@/features/auth/services/authCleanup");
        cleanupAuthState();
      } catch (e) {
        // Ignore cleanup errors
      }
      // COMMENTED OUT: Supabase sign out - we're only using Firebase auth
      // try {
      //   await supabase.auth.signOut({ scope: "global" });
      // } catch (e) {
      //   // Ignore sign out errors
      // }
      try {
        if (firebaseAuth) {
          await firebaseAuth.signOut();
        }
      } catch (e) {
        // Ignore Firebase sign out errors
      }

      // Sign-out returns the user to the classic web app at the origin root
      // (roboticimaging.ai/ on prod), not the site-manager sign-in page.
      window.location.href = `${window.location.origin}/`;
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signOut,
    redirectToUserDashboard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
